import { logout } from '@composables/session';
import { useAuthStore } from '@stores/authStore';
import { useQueryClient } from '@tanstack/vue-query';
import {
  notificationCountQueries,
  notificationsKeys,
  officesMutations,
  usersMutations,
} from '@therame/api-client';
import { Role } from '@therame/shared';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useOfficeStore } from './officeStore';

export const useIndexStore = defineStore('index', () => {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();
  const isCollapse = ref<boolean>(false);
  /**
   * The size of the Offen lane of the Posteingang — what the bell badge shows
   * since PRD #1453. It means "this much awaits you", not "this much you have
   * not glanced at".
   */
  const openNotificationCount = ref<number>(0);
  const loadingRoute = ref<boolean>(false);

  const updateNotificationCount = async () => {
    if (authStore.authContext?.user?.role === Role.SUPERADMIN || !authStore.subjectOnboarded()) {
      return;
    }

    openNotificationCount.value = await queryClient.fetchQuery({
      queryKey: notificationsKeys.openCount(authStore.authContext!.user!.id!),
      queryFn: () => notificationCountQueries.getOpenCount(authStore.authContext!.user!.id!),
      staleTime: 0,
    });
  };

  const clear = () => {
    // this.$reset();
    isCollapse.value = false;
    openNotificationCount.value = 0;
    loadingRoute.value = false;
  };

  const deleteOffice = async () => {
    const authStore = useAuthStore();
    const officeStore = useOfficeStore();

    await officesMutations.delete(authStore.authContext!.user!.officeId!);
    logout();
    officeStore.clear();
  };

  const deleteUser = async (options: { userId?: string; newTherapistId?: string }) => {
    if (!options.userId) {
      return;
    }
    const authStore = useAuthStore();
    const officeStore = useOfficeStore();

    if (options?.newTherapistId) {
      await usersMutations.deleteWithNewTherapist(options.userId, options.newTherapistId);
    } else {
      await usersMutations.delete(options.userId);
    }

    if (options.userId === authStore.authContext?.user?.id) {
      logout();
      officeStore.clear();
    }
  };

  return {
    isCollapse,
    openNotificationCount,
    loadingRoute,
    updateNotificationCount,
    clear,
    deleteOffice,
    deleteUser,
  };
});
